// Small maintained token -> tag table, the same "curated metadata, not derived on-chain" shape
// contracts.js's own CHAIN_META already uses for chains. A vault's own tags fall out
// automatically from whichever two tokens its pool already uses - deploying a new vault needs no
// separate tagging step. A token absent from this table simply contributes no tag, never a
// guessed one - see VaultsSection.jsx's own filter bar for how these are consumed.

import { ADDRESSES, ZERO_ADDR } from '@/lib/contracts'


// The stablecoins this app knows about. USDx is stablenet.tech's, hUSD is Horizon's own
// facilitator-minted one - an earlier version of this comment called both "this protocol's own",
// which is wrong for USDx and is the kind of thing that leads to reasoning about its liquidity and
// its oracle as if Horizon controlled them. Only the QUOTE-RANKING below depends on this set, so
// nothing breaks either way; the comment is what mattered.
//
// HORIZON is the protocol/utility token, never a stablecoin, regardless of which pool it trades
// against. `hUSD` is unset (zero address) on some deployments (not every network has it live yet) -
// filtered out rather than matching every token pair by accident.
const STABLECOINS = new Set(
  [ADDRESSES.USDX, ADDRESSES.hUSD]
    .filter((a) => a && a.toLowerCase() !== ZERO_ADDR)
    .map((a) => a.toLowerCase())
)

export const TAG_LABELS = {
  stablecoin: 'Stablecoin pair',
}

/// Only tags a pair 'stablecoin' when BOTH sides are recognized stablecoins - matching what
/// "Stablecoin pair" actually means (a low-volatility pair, e.g. USDx/hUSD), not "includes one
/// stablecoin leg" (HORIZON/USDx is a volatile/stable pair, not a stablecoin pair).
export function tagsForPair(t0, t1) {
  const a = (t0 || '').toLowerCase()
  const b = (t1 || '').toLowerCase()
  const tags = []
  if (STABLECOINS.has(a) && STABLECOINS.has(b)) tags.push('stablecoin')
  return tags
}

/// How strongly a token reads as the QUOTE side of a pair name. Higher wins the right-hand slot.
///
/// A binary stablecoin test was the previous rule, and it only ordered pairs with a stablecoin in
/// them. Everything else fell through to Uniswap's address sort, which is why TITANX/WETH rendered
/// as "WETH-TITANX" — WETH simply has the lower address. Address order is meaningless to a reader,
/// so any pair of two non-stables was being named by coin flip.
///
/// A ladder rather than a second boolean, because "is a quote asset" is relative: TITANX quotes
/// ORX and DRAGONX, and is itself quoted by WETH. One rank per token expresses that; two booleans
/// cannot.
///
/// Keyed by ADDRESS rather than symbol, matching this file's own rule elsewhere — a token calling
/// itself "WETH" must not win the quote slot on the strength of its ticker. The TitanX-ecosystem
/// addresses are written down here the same way `scripts/createLaunchVaults.cjs` writes them down,
/// for the same reason: they are immutable mainnet constants.
const QUOTE_RANK = new Map([
  ['0xf19308f923582a6f7c465e5ce7a9dc1bec6665b1', 2], // TITANX - quotes ORX, DRAGONX, AURYN
  ['0xd536e7a9543cf9867a580b45cec7f748a1fe11ec', 1], // ORX    - quotes AURYN
])

function quoteRank(addr) {
  const a = (addr || '').toLowerCase()
  // HORIZON is this protocol's identity token and is never the quote side, so it takes the base (left)
  // slot in every pair - below even a rank-0 token. Without this, HORIZON/X28 and HORIZON/SHOGUN (both
  // legs rank 0) fell through to the address sort and rendered "X28-HORIZON" / by coin flip.
  if (a && a === (ADDRESSES.HORIZON_TOKEN || '').toLowerCase()) return -1
  if (STABLECOINS.has(a)) return 4
  if (a && a === (ADDRESSES.WETH || '').toLowerCase()) return 3
  return QUOTE_RANK.get(a) ?? 0
}

/// Display-only pair ordering - `token0`/`token1` on the vault contract itself are Uniswap's own
/// address-sort convention (whichever address is numerically smaller is token0), which is why
/// USDx (a lower address than HORIZON) shows as token0 even though HORIZON is this protocol's own
/// identity token. Every actual amount/price/deposit calculation must keep using the contract's
/// real token0/token1 - this only reorders the two symbols for a pair NAME, quote asset last,
/// matching the near-universal "volatile/identity asset first, quote asset last" DeFi UI
/// convention (e.g. "ETH-USDC", never "USDC-ETH"). Falls back to the original order on a tie,
/// where there is no convention to prefer.
export function orderedPairSymbols(t0, t1, symbol0, symbol1) {
  const rankA = quoteRank(t0)
  const rankB = quoteRank(t1)
  if (rankA > rankB) return [symbol1, symbol0]
  return [symbol0, symbol1]
}
